'use client';

import { useState } from 'react';
import { Heart } from 'lucide-react';

interface FavoriteButtonProps {
  bookId: string;
  isFavorite: boolean;
  onToggle: (bookId: string, added: boolean) => void;
}

export default function FavoriteButton({
  bookId,
  isFavorite,
  onToggle,
}: FavoriteButtonProps) {
  const [loading, setLoading] = useState(false);
  const [favorited, setFavorited] = useState(isFavorite);

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (favorited) {
        // Remove from favorites
        const res = await fetch(`/api/favorites?bookId=${bookId}`, {
          method: 'DELETE',
        });
        if (res.ok) {
          setFavorited(false);
          onToggle(bookId, false);
        }
      } else {
        // Add to favorites
        const res = await fetch('/api/favorites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bookId }),
        });
        if (res.ok) {
          setFavorited(true);
          onToggle(bookId, true);
        }
      }
    } catch (error) {
      console.error('Favorites toggle error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      title={favorited ? 'Remove from Favorites' : 'Add to Favorites'}
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        border: favorited ? 'none' : '2px solid white',
        background: favorited
          ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
          : 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: loading ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        opacity: loading ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (!loading) {
          e.currentTarget.style.transform = 'scale(1.1)';
          if (!favorited) {
            e.currentTarget.style.background = 'rgba(240, 147, 251, 0.3)';
          }
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        if (!favorited) {
          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
        }
      }}
    >
      <Heart
        size={20}
        color="white"
        fill={favorited ? 'white' : 'none'}
        strokeWidth={favorited ? 0 : 2}
      />
    </button>
  );
}

